import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { MdDelete } from 'react-icons/md';
import { FaEdit, FaCheck, FaTimes } from 'react-icons/fa';
import { adminService } from '../../services/adminService';

const ApiListings = ({ tabname }) => {
    const toast = useRef(null);
    const [isAdding, setIsAdding] = useState(false);
    const [formData, setFormData] = useState({ apiuri: '', software: '', description: '' });
    const [listings, setListings] = useState([]);
    const [editKey, setEditKey] = useState(null);
    const [editValues, setEditValues] = useState({ apiuri: '', software: '', description: '' });

    useEffect(() => {
        adminService.getapilisting().then((response) => {
            if (response?.status === 200 && Array.isArray(response?.data?.data)) {
                const mapped = response.data.data.map((item) => ({
                    id: item.id,
                    apiuri: item.apiuri,
                    software: item.software,
                    description: item.description || '',
                    key: item.id,
                }));
                setListings(mapped);
            }
        });
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleAdd = () => {
        if (!formData.apiuri.trim() || !formData.software.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Validation', detail: 'API URI and Software are required', life: 3000 });
            return;
        }

        setIsAdding(true);
        adminService.addapilisting({
            apiuri: formData.apiuri.trim(),
            software: formData.software.trim(),
            description: formData.description.trim(),
            type:'add'
        }).then((response) => {
            if (response?.status === 200) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: 'API listing added successfully', life: 3000 });
                const newKey = Date.now();
                setListings((prev) => [...prev, { ...formData, id: response?.data?.id ?? newKey, key: newKey }]);
                setFormData({ apiuri: '', software: '', description: '' });
            } else if (response?.status === 201) {
                toast.current.show({ severity: 'error', summary: 'Unsuccessful', detail: response?.data?.message || 'Failed to add API listing', life: 3000 });
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
            }
        }).catch(() => {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
        }).finally(() => {
            setIsAdding(false);
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleAdd();
    };

    const handleDelete = (key) => {
        const row = listings.find((item) => item.key === key);
        adminService.deleteapilisting(row?.id).then((response) => {
            if (response?.status === 200) {
                setListings((prev) => prev.filter((item) => item.key !== key));
                toast.current.show({ severity: 'success', summary: 'Deleted', detail: 'Entry removed successfully', life: 2000 });
            } else if (response?.status === 201) {
                toast.current.show({ severity: 'error', summary: 'Unsuccessful', detail: response?.data?.message || 'Failed to delete entry', life: 3000 });
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
            }
        }).catch(() => {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
        });
    };

    const handleEditStart = (item) => {
        setEditKey(item.key);
        setEditValues({ apiuri: item.apiuri, software: item.software, description: item.description || '' });
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditSave = (key) => {
        if (!editValues.apiuri.trim() || !editValues.software.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Validation', detail: 'API URI and Software are required', life: 3000 });
            return;
        }

        const row = listings.find((item) => item.key === key);

        adminService.addapilisting({
            apiuri: editValues.apiuri.trim(),
            software: editValues.software.trim(),
            description: editValues.description.trim(),
            type: 'edit',
            id: row?.id,
        }).then((response) => {
            if (response?.status === 200) {
                setListings((prev) =>
                    prev.map((item) => item.key === key ? { ...item, apiuri: editValues.apiuri.trim(), software: editValues.software.trim(), description: editValues.description.trim() } : item)
                );
                setEditKey(null);
                toast.current.show({ severity: 'success', summary: 'Updated', detail: 'Entry updated successfully', life: 2000 });
            } else if (response?.status === 201) {
                toast.current.show({ severity: 'error', summary: 'Unsuccessful', detail: response?.data?.message || 'Failed to update API listing', life: 3000 });
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
            }
        }).catch(() => {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
        });
    };

    const handleEditCancel = () => {
        setEditKey(null);
    };

    return (
        <>
            <Toast ref={toast} />
            <div className='card-style-padding'>
                <h6 style={{ fontWeight: 600, color: '#1a1f3a', marginBottom: '18px' }}>{tabname}</h6>

                {/* Input row */}
                <div className='row' style={{ marginBottom: '20px' }}>
                    <div className='col-md-3'>
                        <label style={{ fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>
                            API URI <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            type='text'
                            name='apiuri'
                            className='form-control'
                            placeholder='Enter API URI...'
                            value={formData.apiuri}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                        />
                    </div>
                    <div className='col-md-3'>
                        <label style={{ fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>
                            Software <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            type='text'
                            name='software'
                            className='form-control'
                            placeholder='Enter software...'
                            value={formData.software}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                        />
                    </div>
                    <div className='col-md-3'>
                        <label style={{ fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>
                            Description
                        </label>
                        <input
                            type='text'
                            name='description'
                            className='form-control'
                            placeholder='Enter description...'
                            value={formData.description}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                        />
                    </div>
                    <div className='col-md-2' style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Button
                            label='Add'
                            icon='pi pi-plus'
                            severity='success'
                            rounded
                            loading={isAdding}
                            onClick={handleAdd}
                            style={{ height: '38px', width: '100%' }}
                        />
                    </div>
                </div>

                {/* Table */}
                {listings.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        color: '#9a9ab0',
                        fontSize: '14px',
                        background: '#fafbff',
                        borderRadius: '10px',
                        border: '1px dashed #d0d5f0'
                    }}>
                        No entries yet. Fill in the fields above and click Add.
                    </div>
                ) : (
                    <table className='table-bordered custom-table-style tms-table' style={{ width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={{ width: '50px' }}>#</th>
                                <th>API URI</th>
                                <th>Software</th>
                                <th>Description</th>
                                <th style={{ width: '80px', textAlign: 'center' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {listings.map((item, index) => (
                                <tr key={item.key}>
                                    <td>{index + 1}</td>
                                    <td style={{ textAlign: 'left', paddingLeft: '8px' }}>
                                        {editKey === item.key ? (
                                            <input
                                                type='text'
                                                name='apiuri'
                                                className='form-control'
                                                value={editValues.apiuri}
                                                onChange={handleEditChange}
                                                autoFocus
                                            />
                                        ) : item.apiuri}
                                    </td>
                                    <td>
                                        {editKey === item.key ? (
                                            <input
                                                type='text'
                                                name='software'
                                                className='form-control'
                                                value={editValues.software}
                                                onChange={handleEditChange}
                                            />
                                        ) : item.software}
                                    </td>
                                    <td>
                                        {editKey === item.key ? (
                                            <input
                                                type='text'
                                                name='description'
                                                className='form-control'
                                                value={editValues.description}
                                                onChange={handleEditChange}
                                            />
                                        ) : item.description}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <div className='container-box' style={{ justifyContent: 'center', gap: '10px' }}>
                                            {editKey === item.key ? (
                                                <>
                                                    <FaCheck
                                                        className='edit-btn-style custom-cursor-style'
                                                        style={{ fontSize: '17px' }}
                                                        onClick={() => handleEditSave(item.key)}
                                                        title='Save'
                                                    />
                                                    <FaTimes
                                                        className='delete-btn-style custom-cursor-style'
                                                        style={{ fontSize: '17px' }}
                                                        onClick={handleEditCancel}
                                                        title='Cancel'
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <FaEdit
                                                        className='edit-btn-style custom-cursor-style'
                                                        style={{ fontSize: '17px' }}
                                                        onClick={() => handleEditStart(item)}
                                                        title='Edit'
                                                    />
                                                    <MdDelete
                                                        className='delete-btn-style custom-cursor-style'
                                                        style={{ fontSize: '19px' }}
                                                        onClick={() => handleDelete(item.key)}
                                                        title='Delete'
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
};

export default ApiListings;
